import dayjs from 'dayjs'
import { utils } from '@digital-commerce/shared-utils'
import {
  allPass,
  divide,
  equals,
  gte,
  ifElse,
  isEmpty,
  isNil,
  modulo,
  pipe,
  prop,
  propOr,
  __
} from 'ramda'
import { formatCurrency } from './currency'
import {
  getIRPCMonthlyDiscounts,
  formatHSEppDiscount,
  getHSEppDiscount,
  getServiceWithoutSpecifiedDiscounts
} from './homeSolutions'
import { getRecurringPromotionsWhereDurationIsLessThanItemRecurringDuration } from './cart'

const { getMonthlyItemPrice, getMonthlyDiscounts } = utils.cart

export const hasRemainingDurationAndEndDate = (discount) => {
  return !!(discount?.remainingDuration && discount?.endDate)
}

export const getDiscountNameWithRemainingDurationDate = (discount) => {
  const { name, recurringDuration, remainingDuration, endDate } = discount

  if (hasRemainingDurationAndEndDate(discount) && recurringDuration !== remainingDuration) {
    const discountEndDate = dayjs(endDate)
    const discountEndDateFormatted = discountEndDate.format('MMMM D, YYYY')
    return `${name} (expires on ${discountEndDateFormatted})`
  }

  return discount.name
}

export const getDiscountDurationLabel = (discount, translate) => {
  return translate('priceBreakdown.discountLabel', {
    replacements: {
      numberOfYear: divide(__, 12)(discount.duration)
    }
  })
}

export const getDiscountTermText = (discount, translate) => {
  const DEFAULT_TERM = 24

  return `${translate('priceBreakdown.discountLabel', {
    replacements: {
      numberOfYear: propOr(DEFAULT_TERM, 'recurringDuration', discount) / 12
    }
  })}`
}

export const getAlterationDisplayName = ({ name, displayName }) => {
  return isNil(displayName) || isEmpty(displayName) ? name : displayName
}

export const getGroupedDiscountDisplayName = ifElse(
  hasRemainingDurationAndEndDate,
  getDiscountNameWithRemainingDurationDate,
  getDiscountDurationLabel
)

export const getDiscountDisplayName = ifElse(
  hasRemainingDurationAndEndDate,
  getDiscountNameWithRemainingDurationDate,
  getAlterationDisplayName
)

/**
 * Creates a list of month to month recurring discounts from an HS offer.
 * This is a simplicfication of the <RecurringDiscount /> component.
 * Note: This function will not group the discounts as month to month
 * discounts are never grouped. Also this function is not for coupon codes.
 * @param {object} service Top-level HS offer
 * @param {string} lang Language from cookies
 * @param {Function} translate Translate function from hook
 * @returns {[{
 *  name: string,
 *  price: string,
 *  id: string,
 *  prefixIcon: 'DEALS'
 * }]} List of month to month recurring discounts to be displayed
 * */
export const buildHSUnGroupedRecurringDiscountList = (item, lang, translate, showMtmDiscounts) => {
  const monthlyDiscounts = getMonthlyDiscounts(item)

  const termDiscountFilter = allPass([gte(__, 1), pipe(modulo(__, 12), equals(0))])
  const mtmDiscountFilter = allPass([equals(0)])

  const mtmDiscounts = monthlyDiscounts?.filter(
    pipe(prop('duration'), showMtmDiscounts ? mtmDiscountFilter : termDiscountFilter)
  )

  return mtmDiscounts?.map((discount, index) => ({
    name: getDiscountDisplayName(discount, translate),
    price: `-${formatCurrency(discount.amount, lang)}/mo.`,
    id: `${item.id}-discount-${index}`,
    prefixIcon: 'DEALS'
  }))
}

/**
 * Builds grouped recurring discounts data for HS offers.
 * To be used with <NameAndPrice /> component to render data.
 * @param {object} service Top-level HS offer
 * @param {string} lang Language from cookies
 * @param {Function} translate Translate function from hook
 * @returns {[{
 *  name: string,
 *  price: string,
 *  id: string,
 *  prefixIcon: 'DEALS'
 * }]}
 */
export const buildHSGroupedRecurringDiscountList = (service, lang, translate, showMtmDiscounts) => {
  const { monthlyDiscounts } = getMonthlyItemPrice(service)

  const termDiscountFilter = allPass([gte(__, 1), pipe(modulo(__, 12), equals(0))])
  const mtmDiscountFilter = allPass([equals(0)])

  const filteredMonthlyDiscounts = monthlyDiscounts.filter(
    pipe(prop('duration'), showMtmDiscounts ? mtmDiscountFilter : termDiscountFilter)
  )

  return filteredMonthlyDiscounts?.map((discount, index) => ({
    name: getGroupedDiscountDisplayName(discount, translate),
    price: `-${formatCurrency(discount.amount, lang)}/mo.`,
    id: `${service.id}-discount-${index}`,
    prefixIcon: 'DEALS'
  }))
}

export const buildIRPCMonthlyDiscountsList = (service, translate, lang, isAffiliates) => {
  // Business rule: EPP discount must be displayed as a separate line item, and is displayed last
  let irpcMonthlyDiscountsList = []

  const eppDiscount = getHSEppDiscount(service)
  const serviceWithoutEppDiscount = getServiceWithoutSpecifiedDiscounts(service, [eppDiscount])
  const irpcMonthlyDiscounts = getIRPCMonthlyDiscounts(serviceWithoutEppDiscount)

  const formattedMonthlyDiscounts = irpcMonthlyDiscounts?.dueMonthly?.map((discount) => ({
    id: discount.promotionId,
    name: getDiscountDisplayName(discount),
    price: `-${formatCurrency(discount.amount, lang)}/mo.`,
    prefixIcon: 'DEALS'
  }))

  if (formattedMonthlyDiscounts) irpcMonthlyDiscountsList = formattedMonthlyDiscounts
  if (eppDiscount) irpcMonthlyDiscountsList.push(formatHSEppDiscount(eppDiscount, translate, lang, isAffiliates))

  return irpcMonthlyDiscountsList.length > 0 ? irpcMonthlyDiscountsList : undefined
}

export const buildIRPCOneTimeDiscountsList = (service, lang) => {
  const irpcMonthyDiscounts = getIRPCMonthlyDiscounts(service)

  return irpcMonthyDiscounts?.otherCharges?.map((discount) => ({
    id: discount.promotionId,
    name: getDiscountDisplayName(discount),
    price: `-${formatCurrency(discount.amount, lang)}`,
    prefixIcon: discount?.isCouponCode ? 'PIGGYBANK' : 'DEALS'
  }))
}

/**
 * Loops through a list of HS items to get non-recurring discounts that are less than term.
 * @param {object[]} item HS items - can be a top-level offer or a list of sub-items
 * @param {string} lang Language from contexts
 * @returns {[{
 *  id: string,
 *  name: string,
 *  price: string,
 *  prefixIcon: 'DEALS' | 'PIGGYBANK'
 * }]}
 */
export const buildHSNonRecurringDiscounts = (item, lang) => {
  const nonRecurringDiscounts =
    getRecurringPromotionsWhereDurationIsLessThanItemRecurringDuration(item)

  return nonRecurringDiscounts?.map((discount, idx) => {
    const id = discount.id || discount.promotionId || `${item[idx].id}-${idx}`

    return {
      id,
      name: getDiscountDisplayName(discount),
      price: `-${formatCurrency(discount.amount, lang)}`,
      prefixIcon: discount.isCouponCode ? 'PIGGYBANK' : 'DEALS'
    }
  })
}
